import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useLatest } from 'react-use';

import { loadTilesetsMetadata } from '../../utils/loadTilesetsMetadata';
import { TILESET_RASTER_OPACITY } from '../../constants/map';
import { useToggleMap } from '../../hooks/useToggleMap';
import { TILESET_LAYER_PREFIX } from '../../constants/tilesets';
import { layerIdForTileset } from '../../utils/tilesets';
import {
  DRAWN_FEATURE_ID_PREFIX,
  INIT_SELECTED_FEATURE_IDS_STATE,
  TILESET_SPEC,
} from './constants';
import { getSelectedFeatures, layerForTileset } from './helpers';
import suitabilityAcrossLayers from '../../utils/webgl/suitability-calculations/suitabilityAcrossLayers';

/**
 * Disables pinch zoom for whole page.
 */
export const useDisablePageZoom = () => {
  useEffect(() => {
    const tag = document.querySelector('meta[name="viewport"]');
    if (!tag) return null;

    const initValue = tag.getAttribute('content');

    tag.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0',
    );

    return () => {
      tag.setAttribute('content', initValue);
    };
  }, []);
};

/**
 * Load the tileset metadata.
 */
export const useTilesetsData = () => {
  const [tilesets, setTilesets] = useState([]);
  useEffect(() => {
    (async () => {
      const fetchedData = await loadTilesetsMetadata();

      const tilesetSpecWithData = TILESET_SPEC.map((spec) => {
        const tilesetData = (fetchedData ?? []).find(
          (tileset) => tileset.name === spec.name,
        );
        return (
          tilesetData && {
            ...spec,
            ...tilesetData,
          }
        );
      }).filter(Boolean);

      setTilesets(tilesetSpecWithData);
    })();
  }, []);

  return tilesets;
};

/**
 * When selectedTilesetId changes, update the visibility of the tileset layers.
 */
export const useUpdateTilesetVisibility = (
  map,
  tilesets,
  selectedTilesetId,
  shouldShowTileset,
) => {
  useEffect(() => {
    if (!map) return;

    if (selectedTilesetId) {
      // Make selected tileset layer visible
      const selectedLayer = layerForTileset(map, selectedTilesetId);

      if (selectedLayer) {
        selectedLayer.updateOpacity(
          // but don't show the tileset if no land boundaries are selected
          shouldShowTileset ? TILESET_RASTER_OPACITY : 0,
        );
      }
    }

    // Hide other tileset layers
    tilesets
      .filter(({ id }) => id !== selectedTilesetId)
      .forEach(({ id }) => {
        const layer = layerForTileset(map, id);
        if (layer) layer.updateOpacity(0);
      });

    map.triggerRepaint();
  }, [map, selectedTilesetId, shouldShowTileset, tilesets]);
};

/**
 * Manages state for the selected parcels.
 */
export const useSelectedFeatures = (mapRef) => {
  const [userHasSelectedAFeature, setUserHasSelectedAFeature] = useState(false);

  const {
    state: featureSelectionMap,
    toggle: toggleFeatureId,
    reset: resetSelectedFeatures,
    setState: setFeatureSelectionMap,
  } = useToggleMap(INIT_SELECTED_FEATURE_IDS_STATE);

  const selectedFeatureIds = useMemo(
    () =>
      Object.entries(featureSelectionMap)
        .filter(([, value]) => value)
        .map(([key]) => key),
    [featureSelectionMap],
  );

  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const selectedFeatureIdsRef = useLatest(selectedFeatureIds);
  const updateSelectedFeatures = useCallback(() => {
    const features = mapRef.current
      ? getSelectedFeatures(mapRef.current, selectedFeatureIdsRef.current)
      : [];
    setSelectedFeatures(features);
  }, [mapRef, selectedFeatureIdsRef]);

  useEffect(updateSelectedFeatures, [
    updateSelectedFeatures,
    selectedFeatureIds,
  ]);

  // Update the `userHasSelectedAFeature` tracker when the user does that for the first time
  useEffect(() => {
    if (selectedFeatureIds.length > 0 && !userHasSelectedAFeature) {
      setUserHasSelectedAFeature(true);
    }
  }, [selectedFeatureIds, userHasSelectedAFeature]);

  const setSelectedFeatureIds = useCallback(
    (ids) => {
      setFeatureSelectionMap(Object.fromEntries(ids.map((id) => [id, true])));
    },
    [setFeatureSelectionMap],
  );

  return {
    toggleFeatureId,
    resetSelectedFeatures,
    selectedFeatureIds,
    selectedFeatures,
    userHasSelectedAFeature,
    updateSelectedFeatures,
    setSelectedFeatureIds,
  };
};

export const useSuitabilityPerCrop = (map, tilesets, selectedFeatures) => {
  const [suitabilityPerCrop, setSuitabilityPerCrop] = useState({});

  useEffect(() => {
    if (!map) return null;

    // Throttle this function to avoid a bunch of calculations in a row when all the tilesets load
    const calculateSuitability = _.throttle(() => {
      if (!map) return;

      if (tilesets.length === 0 || selectedFeatures.length === 0) {
        setSuitabilityPerCrop({});
        return;
      }

      const suitabilityByLayerId = suitabilityAcrossLayers(
        map,
        tilesets.map((t) => layerIdForTileset(t.id)),
        turf.featureCollection(selectedFeatures),
      );

      const newSuitabilityPerCrop = Object.fromEntries(
        tilesets
          .map(({ id, name, displayName }) => {
            const suitableAcres = Math.round(
              suitabilityByLayerId[layerIdForTileset(id)],
            );
            return [name, { name, displayName, suitableAcres }];
          })
          .filter(Boolean),
      );

      setSuitabilityPerCrop(newSuitabilityPerCrop);
    }, 250);

    // When selected features change, recalculate suitability per crop
    calculateSuitability();

    // When new tileset data loads, recalculate suitability per crop
    const onSourceData = ({ tile, sourceId, isSourceLoaded }) => {
      if (
        !tile ||
        !sourceId.startsWith(TILESET_LAYER_PREFIX) ||
        !isSourceLoaded
      ) {
        return;
      }
      calculateSuitability();
    };
    map.on('sourcedata', onSourceData);
    return () => {
      map.off('sourcedata', onSourceData);
    };
  }, [map, selectedFeatures, tilesets]);

  return suitabilityPerCrop;
};

export const useDrawMode = ({ map, setLayerData: setLayerDataFn }) => {
  const setLayerDataRef = useLatest(setLayerDataFn); // wrap in a ref so we can use it in event handlers

  // NOTE: `isDrawMode` means that the draw mode is active, not that the user is currently drawing
  const [isDrawMode, setIsDrawMode] = useState(false);

  // NOTE: `isDrawing` means that the user is currently drawing (vs selecting).
  const [isDrawing, setIsDrawing] = useState(false);

  const mapboxDrawRef = useRef(null);

  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const featureIsSelected = !!selectedFeatureId;

  // Reset selectedFeatureId when exiting draw mode
  if (!isDrawMode && selectedFeatureId) {
    setSelectedFeatureId(null);
  }

  const NON_DRAWN_FEATURE_ID_PREFIX = 'non-drawn-feature-';

  const drawPolygon = () => {
    const mapboxDraw = mapboxDrawRef.current;
    if (!map || !mapboxDraw || !isDrawMode) return;

    setIsDrawing(true);
    mapboxDraw.changeMode('draw_polygon');
  };

  const deleteFeature = async () => {
    const setLayerData = setLayerDataRef.current;

    const mapboxDraw = mapboxDrawRef.current;
    if (!map || !mapboxDraw || !isDrawMode) return;

    if (!selectedFeatureId) return;

    // Remove the feature from the mapbox draw layer
    const mapboxDrawFeatures = mapboxDraw.getAll().features;
    const mapboxDrawFeature = mapboxDrawFeatures.find(
      (f) => f.properties.id === selectedFeatureId,
    );
    if (mapboxDrawFeature) {
      mapboxDraw.delete(mapboxDrawFeature.id);
    }

    // Remove the feature from the map
    setLayerData((data) => {
      const filteredFeatures = data.features.filter(
        (f) => f.properties.id !== selectedFeatureId,
      );
      return { ...data, features: filteredFeatures };
    }); // (we don't await here -- that way, the user can continue drawing while the map updates)

    // If there are no features left, go back to draw mode
    if (mapboxDraw.getAll().features.length === 0) {
      drawPolygon();
    }

    setSelectedFeatureId(null);
  };

  const deleteAll = () => {
    const setLayerData = setLayerDataRef.current;

    const mapboxDraw = mapboxDrawRef.current;
    if (!map || !mapboxDraw || !isDrawMode) return;

    // remove all features from the mapbox draw layer
    mapboxDraw.deleteAll();

    // remove all features from the map
    setLayerData(() => turf.featureCollection([]));

    // go back to draw mode
    drawPolygon();
  };

  useEffect(() => {
    const setLayerData = setLayerDataRef.current;

    if (!map || !isDrawMode) return null;

    const mapboxDraw = new MapboxDraw({
      displayControlsDefault: false,
      defaultMode: 'draw_polygon',
    });
    mapboxDrawRef.current = mapboxDraw;
    map.addControl(mapboxDraw);
    setIsDrawing(true);

    // Update map data as user edits features:
    const updateData = () => {
      const data = mapboxDraw.getAll();

      // Add IDs to the features
      const dataWithIds = { ...data };
      dataWithIds.features = data.features.map((feature) => {
        // ensure ids are unique and have the prefix
        let { id } = feature.properties;
        if (
          !id ||
          (!id.startsWith(DRAWN_FEATURE_ID_PREFIX) &&
            !id.startsWith(NON_DRAWN_FEATURE_ID_PREFIX))
        ) {
          id = _.uniqueId(DRAWN_FEATURE_ID_PREFIX);
        }

        // make sure mapbox draw has the id as well
        mapboxDraw.setFeatureProperty(feature.id, 'id', id);

        return {
          ...feature,
          id,
          properties: {
            ...feature.properties,
            id,
          },
        };
      });

      setLayerData(() => dataWithIds);
    };

    // Keep user in draw mode after they finish drawing a feature (?)
    const onSelectionChange = ({ features }) => {
      const [feature] = features;
      setSelectedFeatureId(feature?.properties?.id ?? null);
    };
    const onModeChange = ({ mode }) => {
      setIsDrawing(mode.startsWith('draw'));
    };

    map.on('draw.selectionchange', onSelectionChange);
    map.on('draw.create', updateData);
    map.on('draw.update', updateData);
    map.on('draw.modechange', onModeChange);

    return () => {
      if (map._removed) return; // eslint-disable-line no-underscore-dangle

      mapboxDraw.deleteAll();

      map.removeControl(mapboxDraw);

      map.off('draw.selectionchange', onSelectionChange);
      map.off('draw.create', updateData);
      map.off('draw.update', updateData);
      map.off('draw.modechange', onModeChange);

      mapboxDrawRef.current = null;
      setIsDrawing(false);
    };
  }, [map, isDrawMode, setLayerDataRef]);

  return {
    isDrawMode,
    setIsDrawMode,
    featureIsSelected,
    isDrawing,
    deleteFeature,
    deleteAll,
    drawPolygon,
  };
};
